<template>
  <SListItem
    class="sdui-file-item"
    :class="[{ rename: isRenaming }, { 'sdui-file-item--active': showOptionsModal }]"
    :description="file.meta.subtitle"
    :right-action-icon="hideAction ? 'icon-chevron-right' : ''"
  >
    <template #image>
      <div v-if="isImage">
        <SLightbox
          ref="lightbox"
          :re-fetch-fn="async () => await fetchSingleFile()"
          :file-items="[file]"
          img-container-class="file-img"
        />
      </div>
      <div v-else class="media-item">
        <img :src="image"
             alt=""
             loading="lazy"
             width="36"
             height="36"
        />
      </div>
    </template>

    <template #item-title>
      <div v-if="isRenaming" class="p-0 mt-0 mb-1 small w-100">
        <SInput
          :id="`file-${file.uuid}`"
          ref="filenameInput"
          v-model="changedFilename"
          autofocus
          size="sm"
          :label-text="$t('cloud.item.actions.rename')"
          hide-label
          name="filename"
          class="p-0 m-0 small w-100 input-change-filename"
          @keyup.enter="renameFileOnEnter"
          @blur="renameFileOnBlur"
          @keyup.esc="renameFileOnBlur"
        />
      </div>
      <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
      <b-button
        v-else
        variant="link"
        class="sdui-list-item__click-area text-left p-0 sdui-user-item-button d-flex align-items-center"
        data-test="file-name"
        :data-title="fileName"
        :disabled="cloudFilesSelectMultiple"
        @click="atClick"
      >
        {{ fileName }}

        <!-- wopi support collaboration indicator -->
        <sdui-icon v-if="file.meta.has_wopi_support"
                   class="ml-1"
                   :class="file.is_collaborative ? 'icon-lock-open-o' : 'icon-lock-o'"
        />
      </b-button>
    </template>

    <template v-if="cloudFilesSelectMultiple" #right-action>
      <SCheckbox v-model="selected.selected"
                 name="cloud-selected"
                 :label-text="fileName"
      />
    </template>
    <template v-else-if="!hideAction && !cloudFilesSelectMultiple" #right-action>
      <div v-if="file && file.uuid">
        <div class="aux-spinner-input-left" :class="{ 'spinner-input': $wait.waiting('deleting' + file.uuid) }" />
      </div>
      <div v-if="isRenaming">
        <div class="aux-spinner-input-left" :class="{ 'spinner-input': $wait.waiting('change_name' + file.id) }" />
      </div>
      <div v-if="!isRenaming && !isDeleting" class="d-inline-flex">
        <template v-if="settings.showChevron">
          <sdui-icon v-if="isImage" class="icon-chevron-right d-flex align-items-center text-muted float-right mr-1" />
          <sdui-icon v-else class="right-action-download-icon icon-download-o d-flex align-items-center text-muted float-right mr-1" />
        </template>

        <div class="custom-dropdown channels-edit">
          <transition name="slide-bottom">
            <SDropDown
              :id="`dropdown-${file.uuid}`"
              ref="dropdown"
              variant="link"
              right
              size=""
              no-caret
              lazy
              :toggle-attrs="{ 'data-cy': 'cloud-file-dropdown' }"
              @show="handleDropdown"
              @hide="hideDropdown"
            >
              <template #button-content>
                <div ref="optionsBtn" class="actions-btn auto-height">
                  <span v-if="$wait.waiting(`list-item-${file.uuid}`)" class="actions-btn auto-height btn-secondary">
                    <sdui-loader />
                  </span>
                  <template v-else>
                    <span class="sr-only">{{ $t('cloud.upload.action') }}</span>
                    <sdui-icon bg-variant=""
                               variant="lg"
                               class="icon-more text-muted"
                    />
                  </template>
                </div>
              </template>

              <template v-if="!$wait.waiting(`list-item-${file.uuid}`)">
                <b-dropdown-item-button
                  data-test="dropdown-option-file-details"
                  class="gray-300 text-decoration-none"
                  @click.stop="showDetails()"
                >
                  <sdui-icon class="icon-info-o d-inline-block align-middle text-primary" />
                  <span class="ml-2">
                    {{ $t('cloud.item.actions.details') }}
                  </span>
                </b-dropdown-item-button>

                <template v-if="allowShare">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <b-dropdown-item-button class="gray-300 text-decoration-none" @click.stop="shareFile">
                    <sdui-icon class="icon-share-o d-inline-block align-middle text-primary" />
                    <span class="ml-2">
                      {{ $t('cloud.item.actions.share') }}
                    </span>
                  </b-dropdown-item-button>
                </template>

                <template v-if="$can('rename', file)">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <b-dropdown-item-button data-cy="cloud-file-rename"
                                          class="gray-300 text-decoration-none"
                                          @click.stop="renameFile()"
                  >
                    <sdui-icon class="icon-edit-o d-inline-block align-middle text-primary" />
                    <span class="ml-2">
                      {{ $t('cloud.item.actions.rename') }}
                    </span>
                  </b-dropdown-item-button>
                </template>

                <template v-if="file.meta.has_wopi_support && $can('collaborate', file)">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <SLink
                    data-cy="cloud-file-edit-online"
                    class="gray-300 text-decoration-none"
                    href="#"
                    target="_blank"
                    rel="noopener"
                    appearance="b-dropdown-item"
                    @click.stop="submitWopiForm"
                  >
                    <sdui-icon class="icon-filesedit-o d-inline-block align-middle text-primary" />
                    <span class="sr-only">{{ $t('accessibility.external_url') }}</span>
                    <span class="ml-2">
                      {{ $t('cloud.item.actions.edit_online') }}
                    </span>
                  </SLink>
                </template>

                <template v-if="$can('move', file)">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <b-dropdown-item-button
                    class="gray-300 text-decoration-none"
                    data-cy="cloud-file-move"
                    @click.stop="closeOptionsModalAndEmitMove"
                  >
                    <sdui-icon class="icon-forward-o d-inline-block align-middle text-primary" />
                    <span class="ml-2">
                      {{ $t('cloud.item.actions.move') }}
                    </span>
                  </b-dropdown-item-button>
                </template>

                <template v-if="$can('download', file)">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <SLink
                    class="gray-300 text-decoration-none"
                    :href="file.meta.download_uri"
                    data-cy="cloud-folder-download"
                    appearance="b-dropdown-item"
                    :download="file.name"
                    @click.stop="closeOptionsModal"
                  >
                    <sdui-icon class="icon-download-o d-inline-block align-middle text-primary" />
                    <span class="mx-2" aria-hidden="true">
                      {{ $t('cloud.item.actions.download') }} {{ type === 'folder' ? folderSize : $getFileTypeAndSize(file) }}</span>
                    <span class="sr-only">{{ $t('cloud.item.actions.download') }} "{{ file.name }}"
                      {{ type === 'folder' ? folderSize : $getFileTypeAndSize(file) }}</span>
                    <sdui-loader v-if="$wait.waiting(`dropdown-${file.uuid}`)" class="text-right w-100" />
                  </SLink>
                </template>

                <!-- User can rename file -> also able to change the alt-text -->
                <template v-if="isImage && $can('rename', file)">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <b-dropdown-item-button data-test="dropdown-option-add-alt-text" @click="openAltTextModal">
                    <sdui-icon class="icon-note-o primary" />
                    <span class="ml-2 d-inline-block align-middle">{{
                      file.alt_name ? $t('ui.alternative_text.edit') : $t('ui.alternative_text.add')
                    }}</span>
                  </b-dropdown-item-button>
                </template>

                <template v-if="$can('change-collaboration', file)">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <b-dropdown-item-button
                    class="gray-300 text-decoration-none d-flex align-items-center wrapper-with-loader"
                    @click="wopiFileToggleCollaboration"
                  >
                    <sdui-loader v-if="$wait.waiting(`toggle-collaboration`)" class="p-3 mr-1" />
                    <sdui-icon
                      v-else
                      class="d-inline-block align-middle text-primary"
                      :class="file.is_collaborative ? 'icon-lock-o' : 'icon-lock-open-o'"
                    />
                    <span class="ml-2">
                      {{ $t('cloud.item.actions.collaboration', !file.is_collaborative ? 1 : 2) }}
                    </span>
                  </b-dropdown-item-button>
                </template>

                <template v-if="$can('delete', file)">
                  <li class="dropdown-divider" aria-hidden="true" />
                  <b-dropdown-item-button
                    class="delete-action gray-300 text-decoration-none"
                    data-cy="cloud-file-delete"
                    @click.stop="beforeDelete"
                  >
                    <sdui-icon class="icon-delete-o d-inline-block align-middle text-primary" />
                    <span class="ml-2">
                      {{ $t('cloud.item.actions.delete') }}
                    </span>
                  </b-dropdown-item-button>
                </template>
              </template>
            </SDropDown>
          </transition>
        </div>
      </div>
      <div v-if="isRenaming && !isDeleting">
        <b-button
          variant="transparent"
          class="reset-button primary font-weight-bolder"
          :class="{ 'd-none': $wait.waiting('change_name' + file.id) }"
          @click.stop="renameFileOnEnter"
        >
          {{ $t('cloud.item.actions.ok') }}
        </b-button>
      </div>
    </template>

    <template #modals>
      <SConfirmModal
        v-model="showDeleteModal"
        :title="type === 'file' ? $t('cloud.item.delete_file_confirmation') : $t('cloud.item.delete_folder_confirmation')"
        :description="file && file.name ? file.name : ''"
        :ok-title="$t('cloud.item.delete_file.submit')"
        :cancel-title="$t('global.cancel')"
        return-focus="#options-modal__BV_toggle_"
        data-cy="deactivate-modal"
        @ok="deleteFile"
        @hide="showDeleteModal = false"
      />

      <add-edit-alt-text
        v-if="isImage && file"
        :return-focus="`#dropdown-${file.uuid}__BV_toggle_`"
        :show-modal="showAltTextModal"
        :image-preview-src="file.meta.thumbnail_uri || file.meta.uri"
        :current-alt-text="file.alt_name"
        :is-waiting="$wait.waiting('submit-alt-text')"
        @submit="(newAltText) => updateAltText(newAltText)"
        @close="closeAltTextModal"
      />
    </template>
  </SListItem>
</template>

<script>
import { File } from 'src/store/models/channel/index.js'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import VueScrollTo from 'vue-scrollto'
import isMobileDeviceMixing from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import native from 'src/config/native.js'
import fileMixin from 'src/mixins/channels/fileMixin.js'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import AddEditAltText from 'src/components/miscellaneous/ui/dialogs/AddEditAltText.vue'
import Channels from 'src/api/channels.js'
import {defineAsyncComponent} from 'vue'

const SLightbox = defineAsyncComponent(() => import('@/components/base/SLightbox/SLightbox.vue'))

export default {
  name: 'SduiListFileItem',
  components: {SLightbox, AddEditAltText, SduiLoader, SCheckbox },
  mixins: [isMobileDeviceMixing, fileMixin],
  props: {
    file: {
      type: Object || null,
      default: null,
    },

    hideAction: {
      type: Boolean,
      default: false,
    },

    cloudFilesSelectMultiple: {
      type: Boolean,
      default: false,
    },

    selectAll: {
      type: Boolean,
      default: false,
    },

    folderSize: {
      type: String,
      default: null,
    },
  },

  data: () => ({
    isRenaming: false,
    moveFileModal: false,
    pseudoDropdown: false,
    detailsModal: false,
    currentFile: {},
    changedFilename: '',
    showOptionsModal: false,
    initialDropdownFetch: true,
    okButton: false,
    temp: false,
    isDeleting: false,
    showDeleteModal: false,
    scrollIntoViewOptions: {
      behavior: 'smooth',
      block: 'start',
      opt_center: true,
    },

    selected: {},
    canShare: false,
    showAltTextModal: false,
  }),

  computed: {
    isImage() {
      if (!this.file) return false
      return this.file.can_show_preview || this.file.type?.startsWith('image')
    },

    allowShare() {
      return this.isMobile && this.type === 'file' && this.canShare
    },

    /* check where the file comes from / which kind */
    type() {
      return this.file && this.file.file_type === 'FILE' ? 'file' : 'folder'
    },

    fileName() {
      return this.file.name
    },

    image() {
      return this.getIconImage(this.imageIcon)
    },

    imageIcon() {
      if (this.file?.reserved === 'chat_attachment') return 'folder-chat'
      if (this.file?.upload_limited_at) return 'folder-limited-upload'
      if (this.file?.is_protected_folder) return 'folder-upload'
      if (this.type === 'folder') return 'folder'
      return this.getFileIconName(this.file)
    },

    user() {
      return this.file.user || null
    },

    // returns the user which has uploaded the file
    author() {
      return this.file.meta?.username
    },

    /* Todo: is going to be replaces by v-can / this.type */
    settings() {
      return {
        showChevron: this.file?.file_type === 'FILE',
      }
    },
  },

  emits: ['selectedFolder', 'delete', 'openDetails', 'rename', 'move', 'fetch', 'updatedSelectedFiles'],

  methods: {
    // happens when the user is trying to get deeper into the menu
    async atClick() {
      // a user dont want to go deeper while renaming a file or while multiple files are selected
      if (this.isRenaming || this.showOptionsModal || this.cloudFilesSelectMultiple) {
        return
      }

      // return if type is image or file
      if (this.isImage) return this.$refs.lightbox.$refs.imageItem[0].click()
      if (this.type === 'file') {
        await this.fetchFileDetails()
        if (!this.$can('download', this.file)) {
          this.$toastNotify({
            type: 'error',
            text: this.$t('settings.profile.no_user_permissions'),
          })
          return
        }
        return this.handleDownload(`list-item-${this.file.uuid}`)
      }
      if (this.isDeleting) return

      /* emitting actions */
      if (this.type === 'file') return
      this.$emit('selectedFolder', this.file)
    },

    async fetchFileDetails() {
      try {
        this.$wait.start(`list-item-${this.file.uuid}`)
        const file = await this.fetchSingleFile()
        this.$emit('fetch', file)
      } finally {
        this.$wait.end(`list-item-${this.file.uuid}`)
      }
    },

    async handleDropdown(event) {
      if (!this.initialDropdownFetch) return

      event.preventDefault()
      await this.fetchFileDetails()
      this.showDropdown()
      this.initialDropdownFetch = false
    },

    /* dropdown actions */
    async beforeDelete() {
      this.showDeleteModal = true
      this.hideDropdown()
    },

    deleteFile: waitFor('deleteProcess', async function () {
      const token = `deleting${this.file.uuid}`
      try {
        this.$wait.start(token)
        this.isDeleting = true
        this.showDeleteModal = false

        await this.file.deleteFile()
        this.$emit('delete', this.file.uuid)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {}
      this.isDeleting = false
      this.$wait.end(token)
    }),

    showDetails() {
      this.$emit('openDetails', {
        id: this.file.uuid,
        type: this.file.file_type,
        api: this.file.meta.details_uri,
        isProtected: Boolean(this.file.is_protected_folder),
        uploadLimitedAt: this.file?.upload_limited_at,
      })
      this.hideDropdown()
    },

    downloadFile() {
      this.hideDropdown()
    },

    async shareFile() {
      this.hideDropdown()
      await native.share({
        title: this.file && this.file.name,
        url: this.file && this.file.meta.uri,
      })

      this.hideDropdown()
    },

    closeOptionsModalAndEmitMove() {
      this.hideDropdown()
      this.$emit('move', this.file)
    },

    async closeOptionsModal() {
      await this.$nextTick()
      this.hideDropdown()
    },

    /* file rename process, initial, key events, on blur */
    async renameFile() {
      const options = {
        container: 'body',
        easing: 'ease-out',
        offset: -380,
      }

      this.isRenaming = true
      this.hideDropdown()

      await this.$nextTick()

      setTimeout(() => {
        VueScrollTo.scrollTo(`#wrapper-${this.file.uuid}`, 400, options)
      }, 100)

      if (this.type === 'folder') {
        this.changedFilename = this.file.name
        return
      }

      this.changedFilename = this.file.name.substring(0, this.file.name.length - this.file.extension.length - 1)
    },

    renameFileOnEnter: waitFor('renamingProcess', async function () {
      const token = `change_name${this.file.id}`
      this.$wait.start(token)

      try {
        this.changedFilename = this.file.file_type === 'FILE' ? `${this.changedFilename}.${this.file.extension}` : this.changedFilename
        const res = await File.renameFile(
          this.file.cloud_id,
          this.file && this.file.meta && this.file.meta.absolute_path,
          this.changedFilename,
        )
        this.$emit('rename', res.data)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {}
      this.isRenaming = false
      this.$wait.end(token)
    }),

    async renameFileOnBlur() {
      setTimeout(async () => {
        if (this.$wait.waiting('renamingProcess')) return
        await this.$nextTick()
        this.isRenaming = false
      }, 200)
    },

    wopiFileToggleCollaboration: waitFor('toggle-collaboration', async function () {
      const file = await File.changeCollaboration(this.file)
      this.$emit('fetch', file)
    }),

    submitWopiForm() {
      const { edit_uri, edit_access_token } = this.file.meta
      const ttl = Date.now() + 86400 * 365
      this.closeOptionsModal()
      const routeData = this.$router.resolve({
        name: 'sdui-redirect-page',
        query: { path: edit_uri, data: JSON.stringify({ edit_uri, edit_access_token, ttl }) },
      })
      const router = document.createElement('a')
      router.setAttribute('href', routeData.href)
      router.setAttribute('target', '_blank')
      router.click()
    },

    showDropdown() {
      this.showOptionsModal = true
      this.$refs.dropdown?.$refs?.sdropdown?.show()
    },

    hideDropdown() {
      this.showOptionsModal = false
      this.$refs.dropdown?.$refs?.sdropdown?.hide()
    },

    openAltTextModal() {
      this.showAltTextModal = true
    },

    closeAltTextModal() {
      this.showAltTextModal = false
    },

    updateAltText: waitFor('submit-alt-text', async function (newAltText) {
      if (this.file.alt_name === newAltText) return this.closeAltTextModal()

      const res = await Channels.putChannelCloudByIdFileById(this.file.cloud_id, this.file.uuid, { alt_name: newAltText })
      this.$emit('fetch', res.data)
      this.closeAltTextModal()
    }),
  },

  watch: {
    selectAll: {
      handler: function (selectAll) {
        this.selected = { uuid: this.file.uuid, selected: selectAll }
      },
    },

    'file.uuid': {
      // immediate so uuid is seeded right away, even for items that mount later (e.g. lazily
      // loaded on scroll) while cloudFilesSelectMultiple/selectAll is already active. Always
      // sets selected to false - only an explicit toggle after mount should select an item.
      handler: function (uuid) {
        this.selected = { uuid: uuid, selected: false }
      },

      immediate: true,
    },

    'selected.selected': {
      handler: function (newVal, oldVal) {
        if (oldVal === newVal) return
        this.$emit('updatedSelectedFiles', this.selected)
      },
    },

    cloudFilesSelectMultiple() {
      this.selected = { uuid: this.file.uuid, selected: false }
    },

    file: {
      handler: function (newVal, oldVal) {
        // When the page is losing focus and regains it, "file" is getting re-fetched and losing its "can"-values.
        // This results in dropdown losing most buttons because they are conditioned on "can"-values when refocus happens.
        // In those cases, we just re-initialise the dropdown again so the next click on it will re-fetch the file.
        if (this.$compare(oldVal, newVal)) return
        this.hideDropdown()
        this.initialDropdownFetch = true
      },
    },
  },

  async created() {
    this.canShare = await native.canShare()
  },
}
</script>

<style lang="scss" scoped>
.input-change-filename {
  border: initial;
  border-radius: 0;
  box-shadow: none;
  height: auto;

  &:focus {
    box-shadow: none;
  }
}

.aux-spinner-input-left {
  position: relative;
  width: 20px;
}

.actions-btn {
  background: $color-secondary;
  border-radius: 50% !important;
  height: 30px;
  width: 30px;
  padding: 0 !important;
  display: flex !important;
  align-items: center;
  justify-content: space-around;
}

.sdui-file-item--active {
  position: relative;
  z-index: 1042;
}

:deep(.file-img) {
  display: block;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background-size: cover;
  background-position: center;
}

.sdui-file-item :deep(.sdui-list-item__content) {
  width: 45%;
}

.sdui-file-item button.sdui-user-item-button {
  min-height: inherit;
  color: $color-gray-400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline !important;
  width: 100%;
}

.delete-action {
  border-top: 4px solid $color-secondary;
}

.wrapper-with-loader {
  min-height: 48px;
}

.right-action-download-icon {
  font-size: pxtorem(24);
}
</style>
